import { firstValueFrom } from 'rxjs';
import { ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { HttpClient } from '@angular/common/http';
import { GenericAzureBlobService } from '@shared/src/services/generic-azure-blob.service';

export const encodeText = (text: string): string => {
  if (!text) {
    return '';
  }

  // Convert text to hex string
  return Array.from(text)
    .map((char) => char.charCodeAt(0).toString(16).padStart(4, '0'))
    .join('');
};

export const decodeText = (encodedText: string): string => {
  if (!encodedText) {
    return '';
  }

  // Check if the string is properly encoded (should be a multiple of 4 characters)
  if (encodedText.length % 4 !== 0) {
    return encodedText; // Return as-is if not properly encoded
  }

  // Convert hex string back to text
  const chunks = encodedText.match(/.{1,4}/g) || [];
  return chunks.map((chunk) => String.fromCharCode(parseInt(chunk, 16))).join('');
};

//#region Validators
export function decimalValidator(maxDecimalPlaces: number, isRequired = false, maxDigits = 18): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const { value } = control;
    if (value === null || value === '' || value === undefined) {
      return isRequired ? { required: true } : null;
    }
    const stringValue = value.toString().trim();
    // Check valid decimal with optional decimal part
    const decimalRegex = new RegExp(`^\\d{1,${maxDigits}}(\\.\\d{0,${maxDecimalPlaces}})?$`);
    if (!decimalRegex.test(stringValue)) {
      return { invalidDecimal: true };
    }

    // Split value into integer and decimal parts
    const [integerPart, decimalPart = ''] = stringValue.split('.');

    // Validate total digits (precision)
    const totalDigits = integerPart.length + decimalPart.length;
    if (totalDigits > maxDigits) {
      return { invalidDecimal: true };
    }

    return null;
  };
}

//#endregion

export function decimalwithCommaSepValidator(maxDecimalPlaces: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const { value } = control;
    if (value === null || value === '') {
      return null;
    }
    const regex = new RegExp(`^(\\d{1,3}(,\\d{3})+|\\d+)(\\.\\d{0,${maxDecimalPlaces}})?$`);
    const valid = regex.test(value);

    return valid ? null : { invalidDecimal: true };
  };
}

export function sanitizeNumber(value: any): number | null {
  const cleaned = value?.toString().replace(/,/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
}

const ImageRegexes: RegExp[] = [
  /<img[^>]+src="(https:\/\/[^"]*blob\.core\.windows\.net[^"]*)"[^>]*>/g,
  /<img[^>]+src="(http:\/\/127\.0\.0\.1:10000\/devstoreaccount[^"]*)"[^>]*>/g,
];

export class ImageProcessor {
  public readonly imageUrlMappings = new Map<string, string>();
  async refreshImageUrlsInContent(
    content: string,
    requestId: string,
    azureBlobService: GenericAzureBlobService,
    http: HttpClient,
    messageService: MessageService
  ): Promise<string> {
    if (!content || !requestId) return content;

    try {
      let updatedContent = content;
      const baseUrls: string[] = [];
      let match;

      // Extract all image URLs
      ImageRegexes.forEach((imgRegex: RegExp) => {
        while ((match = imgRegex.exec(content)) !== null) {
          const [baseUrl] = match[1].split('?'); // Get base URL without SAS token
          if (!baseUrls.includes(baseUrl)) {
            baseUrls.push(baseUrl);
          }
        }
      });

      if (baseUrls.length > 0) {
        // Get new signed URLs for all unique base URLs
        const fileNames = baseUrls.map((url) => url.split('/').pop() ?? '');
        const response: any = await firstValueFrom(azureBlobService.getSignedUrl(fileNames, requestId.toString(), 1));

        if (response?.signedUrls) {
          // Download and process each image
          for (let i = 0; i < response.signedUrls.length; i++) {
            try {
              const signedUrl = response.signedUrls[i];
              const blob: Blob = await firstValueFrom(http.get(signedUrl, { responseType: 'blob' }));

              // Convert blob to base64
              const base64 = await this.blobToBase64(blob);

              // Store the mapping
              this.imageUrlMappings.set(base64, baseUrls[i]);

              // Replace the old URL with the base64 data URL
              updatedContent = updatedContent.replace(new RegExp(this.escapeRegExp(baseUrls[i]), 'g'), base64);
            } catch {
              messageService.add({
                severity: 'error',
                summary: 'Error downloading image!',
                detail: '',
                life: 2000,
              });
            }
          }
        }
      }

      return updatedContent;
    } catch {
      messageService.add({
        severity: 'error',
        summary: 'Error refreshing image URLs!',
        detail: '',
        life: 2000,
      });
      return content;
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  public escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
